// Contas das despesas: hodômetro em sequência (a mesma regra do banco),
// consumo do caminhão e parcelas de compra a prazo.
const centavos = (v) => Math.round((Number(v) || 0) * 100);

export const SALTO_MAXIMO_POR_DIA = 1500;

function diasEntre(a, b) {
  const [ya, ma, da] = a.split('-').map(Number);
  const [yb, mb, db] = b.split('-').map(Number);
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86400000);
}

const dataBR = (iso) => iso.split('-').reverse().join('/');
const km = (n) => Number(n).toLocaleString('pt-BR');

// Leituras de hodômetro de um veículo (lançamentos com hodômetro), na ordem.
function emOrdem(leituras) {
  return [...leituras]
    .filter((l) => l.hodometro !== null && l.hodometro !== undefined && !l.deleted_at)
    .sort((a, b) => (a.data === b.data ? String(a.created_at ?? '').localeCompare(String(b.created_at ?? '')) : a.data < b.data ? -1 : 1));
}

// Confere uma leitura nova contra as do mesmo veículo (as mesmas contas do
// gatilho do banco). Devolve null se está tudo certo, ou { tipo, mensagem }.
export function conferirHodometro(leituras, { id = null, data, hodometro }) {
  const outras = emOrdem(leituras.filter((l) => l.id !== id));
  const antes = outras.filter((l) => l.data <= data).at(-1);
  const depois = outras.find((l) => l.data > data);
  if (antes && hodometro < antes.hodometro) {
    return {
      tipo: 'menor',
      mensagem: `${km(hodometro)} km é menor que a leitura de ${dataBR(antes.data)} (${km(antes.hodometro)} km). Confira se o abastecimento é deste caminhão.`,
    };
  }
  if (depois && hodometro > depois.hodometro) {
    return { tipo: 'maior', mensagem: `${km(hodometro)} km é maior que a leitura de ${dataBR(depois.data)} (${km(depois.hodometro)} km).` };
  }
  if (antes && hodometro - antes.hodometro > SALTO_MAXIMO_POR_DIA * Math.max(1, diasEntre(antes.data, data))) {
    return { tipo: 'salto', mensagem: `${km(hodometro - antes.hodometro)} km a mais desde ${dataBR(antes.data)}: mais de ${km(SALTO_MAXIMO_POR_DIA)} km por dia.` };
  }
  return null;
}

const temKm = (a) => a.hodometro !== null && a.hodometro !== undefined;
const litrosSomados = (lista) => Math.round(lista.reduce((s, a) => s + Number(a.litros) * 1000, 0)) / 1000;

// Abastecimentos (com litros) na ordem: data e ordem de lançamento; no mesmo
// dia e momento, o sem hodômetro vem antes do com.
export function abastecimentosEmOrdem(abastecimentos) {
  return abastecimentos
    .filter((a) => !a.deleted_at && Number(a.litros) > 0)
    .sort((a, b) => {
      if (a.data !== b.data) return a.data < b.data ? -1 : 1;
      const ca = String(a.created_at ?? '');
      const cb = String(b.created_at ?? '');
      if (ca !== cb) return ca < cb ? -1 : 1;
      return (temKm(a) ? 1 : 0) - (temKm(b) ? 1 : 0);
    });
}

// Consumo de um abastecimento: km desde a leitura anterior ÷ litros
// abastecidos desde ela (tanque cheio a tanque cheio). Os abastecimentos sem
// hodômetro no meio do caminho entram nos litros.
export function consumoDoAbastecimento(abastecimentos, atual) {
  if (!(Number(atual.litros) > 0) || !temKm(atual)) return null;
  let lista = abastecimentosEmOrdem(abastecimentos);
  let i = atual.id ? lista.findIndex((a) => a.id === atual.id) : -1;
  if (i < 0) {
    // Prévia do formulário: entra depois dos que já estão até a data dele.
    lista = [...lista.filter((a) => a.data <= atual.data), atual];
    i = lista.length - 1;
  }
  const semKm = [];
  for (let j = i - 1; j >= 0; j--) {
    const a = lista[j];
    if (!temKm(a)) {
      semKm.push(a);
      continue;
    }
    if (!(atual.hodometro > a.hodometro)) return null;
    const rodados = atual.hodometro - a.hodometro;
    const litros = litrosSomados([atual, ...semKm]);
    return { km: rodados, litros, kmPorLitro: rodados / litros, desde: a.data, semKm: semKm.length };
  }
  return null;
}

// Consumo médio: km entre a 1ª e a última leitura ÷ litros abastecidos depois
// da 1ª (a fórmula do modelo), contando os abastecimentos sem hodômetro.
export function consumoMedio(abastecimentos) {
  const lista = abastecimentosEmOrdem(abastecimentos);
  const primeiro = lista.findIndex(temKm);
  const ultimo = lista.findLastIndex(temKm);
  if (primeiro < 0 || ultimo <= primeiro) return null;
  const rodados = lista[ultimo].hodometro - lista[primeiro].hodometro;
  const litros = litrosSomados(lista.slice(primeiro + 1, ultimo + 1));
  if (!(rodados > 0) || !(litros > 0)) return null;
  return { km: rodados, litros, kmPorLitro: rodados / litros, abastecimentos: ultimo - primeiro + 1 };
}

// Mesmo dia nos meses seguintes (ou o último dia, em mês mais curto).
export function somarMeses(iso, meses) {
  const [a, m, d] = iso.split('-').map(Number);
  const alvo = new Date(Date.UTC(a, m - 1 + meses, 1));
  const ultimo = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
  alvo.setUTCDate(Math.min(d, ultimo));
  return alvo.toISOString().slice(0, 10);
}

// Divide o total em n parcelas mensais; os centavos que sobram vão na 1ª.
export function dividirParcelas(total, n, primeiroVencimento) {
  const inteiro = centavos(total);
  const base = Math.floor(inteiro / n);
  const sobra = inteiro - base * n;
  return Array.from({ length: n }, (_, i) => ({
    numero: i + 1,
    total: n,
    vencimento: somarMeses(primeiroVencimento, i),
    valor: (base + (i === 0 ? sobra : 0)) / 100,
  }));
}

// O que a empresa tem a pagar: as parcelas em aberto das compras a prazo, o
// agenciamento da transportadora ainda não pago e o acerto em que a empresa
// ficou devendo ao colaborador. Conta pura (a tela "A pagar" e o resumo da
// semana por e-mail usam); o que está na lixeira fica de fora.
export function itensAPagar({ parcelas = [], lancamentos = [], fretes = [], clientes = [], acertos = [], colaboradores = [] }) {
  const vivo = (x) => !x.deleted_at;
  const porId = new Map(lancamentos.filter((l) => vivo(l) && l.tipo === 'despesa' && l.condicao === 'a_prazo').map((l) => [l.id, l]));
  const itens = parcelas
    .filter((p) => vivo(p) && !p.pago_em && porId.has(p.lancamento_id))
    .map((p) => ({ tipo: 'parcela', id: p.id, vencimento: p.vencimento, valor: Number(p.valor), parcela: p, despesa: porId.get(p.lancamento_id) }));
  for (const f of fretes) {
    if (!vivo(f) || f.agenciado_por !== 'transportadora' || f.agenciamento_pago_em || !(Number(f.agenciamento_valor) > 0)) continue;
    itens.push({
      tipo: 'agenciamento', id: f.id, vencimento: f.data_carregamento, valor: Number(f.agenciamento_valor), frete: f,
      cliente: clientes.find((c) => c.id === f.cliente_id) ?? null,
    });
  }
  // Acerto fechado em que a empresa ficou devendo ao colaborador.
  for (const a of acertos) {
    if (!vivo(a) || a.status !== 'fechado' || !(Number(a.saldo) < 0)) continue;
    itens.push({
      tipo: 'acerto', id: a.id, vencimento: a.data, valor: -Number(a.saldo), acerto: a,
      colaborador: colaboradores.find((p) => p.id === a.colaborador_id) ?? null,
    });
  }
  return itens;
}

// Parcelas em aberto, separadas para a tela "A pagar".
export function separarAPagar(itens, hoje) {
  const daqui7 = somarDias(hoje, 7);
  const grupos = { vencidas: [], semana: [], depois: [] };
  for (const item of [...itens].sort((a, b) => (a.vencimento < b.vencimento ? -1 : a.vencimento > b.vencimento ? 1 : 0))) {
    if (item.vencimento < hoje) grupos.vencidas.push(item);
    else if (item.vencimento <= daqui7) grupos.semana.push(item);
    else grupos.depois.push(item);
  }
  const soma = (lista) => lista.reduce((s, x) => s + centavos(x.valor), 0) / 100;
  return { ...grupos, totais: { vencidas: soma(grupos.vencidas), semana: soma(grupos.semana), depois: soma(grupos.depois), tudo: soma(itens) } };
}

export function somarDias(iso, dias) {
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, d + dias)).toISOString().slice(0, 10);
}
